"use client";

/**
 * AudioInitializer
 *
 * Standalone "boot the audio engine on first user gesture" safety net.
 *
 * The browser autoplay policy requires that any AudioContext be created (or
 * resumed) inside a user-gesture handler — otherwise it stays suspended and
 * no sound ever plays. GameSystemsProvider already wires this up for the
 * /game route, but pages like /login, /dex, and the landing page do not
 * mount that provider. This component is a small, dependency-free redundancy
 * that ensures audio boots on ANY page on the first interaction.
 *
 * Behavior:
 *  - On mount (client-only), registers `pointerdown`, `keydown`, and
 *    `touchstart` listeners on `document`, each with `{ once: true }` so the
 *    browser auto-removes them after their first fire.
 *  - On any first interaction:
 *      1. `audio.init()` — creates the AudioContext, resumes if suspended.
 *      2. `audio.loadFromStorage()` — re-applies persisted master/sfx/bgm volumes.
 *      3. `playSfx('menu_open')` — a faint confirmation blip so we know it works.
 *  - Each listener also tears down its siblings (best-effort) so we never
 *    double-init if the user touches the page in two different ways at once.
 *  - SSR-safe: the effect only runs in the browser.
 *
 * Where to mount:
 *  Mount this component in the ROOT layout (`src/app/layout.tsx`), inside
 *  `<body>` and just before `{children}`. That guarantees it is present on
 *  every route (game, login, dex, landing, etc.) without requiring each page
 *  to opt in. It renders nothing, so it is layout-neutral.
 *
 *  Example:
 *    <body>
 *      <AudioInitializer />
 *      {children}
 *    </body>
 */

import { useEffect } from "react";

import { audio, playSfx } from "@/engine/audio";

type GestureEvent = "pointerdown" | "keydown" | "touchstart";

const GESTURE_EVENTS: readonly GestureEvent[] = [
  "pointerdown",
  "keydown",
  "touchstart",
] as const;

export function AudioInitializer(): null {
  useEffect(() => {
    if (typeof document === "undefined") return;

    let booted = false;
    const handlers: Partial<Record<GestureEvent, EventListener>> = {};

    const boot = (): void => {
      if (booted) return;
      booted = true;

      // Best-effort: remove siblings immediately. The { once: true } option
      // already auto-removes whichever listener actually fired; this handles
      // the remaining two so they never fire and try to re-init.
      for (const evt of GESTURE_EVENTS) {
        const fn = handlers[evt];
        if (fn) {
          document.removeEventListener(evt, fn);
        }
      }

      void audio
        .init()
        .then(() => {
          // Re-apply persisted volumes now that the engine has live gain nodes.
          audio.loadFromStorage();
          // Faint confirmation blip. menu_open is a soft 2-note square chirp.
          playSfx("menu_open");
        })
        .catch(() => {
          // init() already swallows its own failures; nothing to do here.
        });
    };

    for (const evt of GESTURE_EVENTS) {
      const fn: EventListener = () => boot();
      handlers[evt] = fn;
      document.addEventListener(evt, fn, { once: true });
    }

    return () => {
      for (const evt of GESTURE_EVENTS) {
        const fn = handlers[evt];
        if (fn) {
          document.removeEventListener(evt, fn);
        }
      }
    };
  }, []);

  return null;
}

export default AudioInitializer;
